import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AppointClientReqBody,
  CSVTemplateReqParams,
  Product,
  ProductCatalog,
  ProductSet,
  ProductStrategiesProductsRequestParams,
  ProductStrategy,
  UpdateCatalogBody
} from '@fb-tool-front/shared/interfaces/catalogs.type';
import { environment } from '@fb-tool-front/environments/environment';
import { forkJoin, Observable, of } from 'rxjs';
import { map, retryWhen, shareReplay, take, tap } from 'rxjs/operators';
import param from 'jquery-param';
import { AuthService } from './auth.service';
import { Client } from '@fb-tool-front/shared/interfaces/auth.type';
import { genericRetryStrategy } from '@fb-tool-front/shared/utilities/utilities';

@Injectable({
  providedIn: 'root'
})
export class CatalogsService {
  isCanvasChanged = false;

  private CATALOG_URL = `${environment.host}/api/overlay/product_catalog`;
  private VIDEO_TOOL_URL = `${environment.host}/api/video`;
  private catalogs: ProductCatalog[];
  private productStrategies: Observable<ProductStrategy[]>;

  constructor(private http: HttpClient, private authService: AuthService) {
    this.authService.redirectToVendor$.subscribe(() => (this.catalogs = null));
  }

  private fillInClientFields([catalogs, allClients]: [ProductCatalog[], Client[]]) {
    return catalogs?.map(el => {
      el.isChecked = el?.client === this.authService.getCurrentClient().id;
      el.isAnotherClient = el?.client && el?.client !== this.authService.getCurrentClient().id;
      if (el.isAnotherClient) {
        el.anotherClient = allClients.find(client => client.id === el.client)?.name;
      }

      return el;
    });
  }

  getCatalogs(withoutCache = false, saveInCache = true): Observable<ProductCatalog[]> {
    if (this.catalogs && !withoutCache) {
      return of(this.catalogs);
    } else {
      return forkJoin([
        this.http.get<ProductCatalog[]>(`${this.CATALOG_URL}`, {
          params: { client: this.authService.getCurrentClient().id }
        }),
        this.authService.allClientsList$.pipe(take(1))
      ]).pipe(
        map((res: [ProductCatalog[], Client[]]) => this.fillInClientFields(res)),
        tap(res => (saveInCache ? (this.catalogs = res) : null))
      );
    }
  }

  getCatalogById(id: string): Observable<ProductCatalog> {
    return forkJoin([
      this.http
        .get<ProductCatalog>(`${this.CATALOG_URL}/${id}`, {
          params: { client: this.authService.getCurrentClient().id }
        })
        .pipe(map(res => [res])),
      this.authService.allClientsList$.pipe(take(1))
    ]).pipe(
      map((res: [ProductCatalog[], Client[]]) => this.fillInClientFields(res)),
      map(res => res[0])
    );
  }

  getProductSets(catalogId: string): Observable<ProductSet[]> {
    return this.http
      .get<ProductSet[]>(`${this.CATALOG_URL}/${catalogId}/product_sets`)
      .pipe(map(response => (response ? response : [])));
  }

  getAllCatalogProducts(id, params): Observable<{ data: Product[]; paging: any }> {
    return this.http
      .get<{ data: Product[]; paging: any }>(`${this.CATALOG_URL}/${id}/products`, {
        params: new HttpParams({ fromString: param(params) })
      })
      .pipe(
        map(res => {
          if (res?.data == null) {
            throw res;
          }

          return res;
        }),
        retryWhen(genericRetryStrategy())
      );
  }

  getStrategies(): Observable<ProductStrategy[]> {
    if (!this.productStrategies) {
      this.productStrategies = this.http
        .get<ProductStrategy[]>(`${this.VIDEO_TOOL_URL}/select_product_strategy`)
        .pipe(shareReplay(1));
    }

    return this.productStrategies;
  }

  getStrategyProducts(
    strategyId,
    params: ProductStrategiesProductsRequestParams
  ): Observable<Product[]> {
    return this.http.get<Product[]>(
      `${this.VIDEO_TOOL_URL}/select_product_strategy/${strategyId}/products`,
      {
        params: new HttpParams({ fromString: param(params) })
      }
    );
  }

  getCSVTemplate(params: CSVTemplateReqParams): Observable<string> {
    return this.http.get(`${this.CATALOG_URL}/csv_files/csv_template`, {
      params: { ...params },
      responseType: 'text'
    });
  }

  uploadCSVFile(body: FormData): Observable<string> {
    return this.http.post<string>(`${this.CATALOG_URL}/csv_files/user_upload`, body);
  }

  appointClients(body: AppointClientReqBody) {
    return forkJoin([
      this.http.post<ProductCatalog[] | null>(`${this.CATALOG_URL}/client`, body),
      this.authService.allClientsList$.pipe(take(1))
    ]).pipe(
      map((res: [ProductCatalog[] | null, Client[]]) => this.fillInClientFields(res)),
      tap(res => (body.no_return ? null : (this.catalogs = res)))
    );
  }

  setCatalogPriorities(catalogId: string, body: { product_set_ids: string[] }) {
    return this.http.post<ProductSet>(`${this.CATALOG_URL}/${catalogId}/overlay/priority`, body);
  }

  hasAitargetUrl(value: string) {
    return (
      !!value?.match(/^https?:\/\/fb\.aitarget\.com\/api\/overlay_feed\/\d+$/) ||
      !!value?.match(/^https?:\/\/fb-tool\.aitarget\.tech\/overlay_feed\/\d+$/) ||
      !!value?.match(/^https?:\/\/fb-tool-master\.aitarget\.com\/overlay_feed\/\d+$/)
    );
  }

  getProductIdByVertical(vertical: string, product: Product) {
    if (!product) {
      return null;
    }

    return {
      commerce: product.retailer_id,
      destinations: product.destination_id,
      hotels: product.hotel_id,
      vehicles: product.vehicle_id
    }[vertical];
  }

  getProductIdKeyByVertical(vertical) {
    return {
      commerce: 'retailer_id',
      destinations: 'destination_id',
      hotels: 'hotel_id',
      vehicles: 'vehicle_id'
    }[vertical];
  }

  updateCatalogById(catalogId: string, body: UpdateCatalogBody): Observable<ProductCatalog> {
    return this.http.patch<ProductCatalog>(`${this.CATALOG_URL}/${catalogId}`, body);
  }
}
